import { useState, type FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { resetPasswordSchema } from '@flymind/shared';
import { useTranslation } from '../hooks/useTranslation';
import { api } from '../lib/api-client';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';

export default function ResetPassword() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    setApiError('');

    const fieldErrors: Record<string, string> = {};

    // Validate password with schema
    const result = resetPasswordSchema.safeParse({
      token: token || '',
      password,
    });

    if (!result.success) {
      for (const issue of result.error.issues) {
        const field = issue.path[0] as string;
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message;
        }
      }
    }

    // Confirm password
    if (password !== confirmPassword) {
      fieldErrors.confirmPassword = t('auth.passwordsDontMatch');
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/auth/reset-password', {
        token,
        password,
      });
      navigate('/login?success=' + encodeURIComponent(t('auth.resetPasswordSuccess')));
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('errors.generic');
      setApiError(message);
    } finally {
      setIsLoading(false);
    }
  }

  if (!token) {
    return (
      <div className="text-center">
        <p className="text-sm text-alert-exceptional">
          {t('auth.tokenMissing')}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 text-center">
        <h2 className="text-xl font-bold text-text-primary">
          {t('auth.resetPassword')}
        </h2>
        <p className="mt-1 text-sm text-text-secondary">
          {t('auth.enterNewPassword')}
        </p>
      </div>

      {apiError && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {apiError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input
          label={t('auth.newPassword')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t('auth.minPasswordPlaceholder')}
          error={errors.password || errors.token}
          required
          hint={t('auth.passwordRequirements')}
          autoComplete="new-password"
        />

        <Input
          label={t('auth.confirmPassword')}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder={t('auth.repeatPasswordPlaceholder')}
          error={errors.confirmPassword}
          required
          autoComplete="new-password"
        />

        <Button type="submit" size="lg" isLoading={isLoading} className="w-full">
          {t('auth.resetPassword')}
        </Button>
      </form>
    </div>
  );
}
